import React from "react";
import { useNavigate } from "react-router-dom";
import { useSpace } from "../../../contexts/SpaceContext";
import {
  Card,
  CardContent,
  Chip,
  Typography,
  Box,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  Widgets as WidgetIcon,
  CalendarMonth as CalendarIcon,
  Event as EventIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";

const SpaceAppCards = ({
  title,
  amount,
  chip,
  to,
  percentagetext,
  percentagecolor,
  description,
}) => {
  const navigate = useNavigate();
  const { currentSpace } = useSpace();

  // Function to handle card click
  const handleCardClick = () => {
    // Navigate to the destination path, adding a query parameter with the current space ID
    navigate(`${to}?spaceId=${currentSpace?.id || ""}`);
  };

  // Get card theme based on type
  const getCardTheme = () => {
    switch (amount) {
      case "Widget":
        return {
          gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          iconBg: "rgba(102, 126, 234, 0.1)",
          iconColor: "#667eea",
          icon: <WidgetIcon sx={{ fontSize: 32 }} />,
          stats: "Live Chat Active",
          statsIcon: <GroupIcon sx={{ fontSize: 16 }} />,
        };
      case "Calendar":
        return {
          gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
          iconBg: "rgba(240, 147, 251, 0.1)",
          iconColor: "#f093fb",
          icon: <CalendarIcon sx={{ fontSize: 32 }} />,
          stats: "Scheduling Ready",
          statsIcon: <ScheduleIcon sx={{ fontSize: 16 }} />,
        };
      case "Events":
        return {
          gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
          iconBg: "rgba(79, 172, 254, 0.1)",
          iconColor: "#4facfe",
          icon: <EventIcon sx={{ fontSize: 32 }} />,
          stats: "Event Platform",
          statsIcon: <TrendingUpIcon sx={{ fontSize: 16 }} />,
        };
      default:
        return {
          gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          iconBg: "rgba(102, 126, 234, 0.1)",
          iconColor: "#667eea",
          icon: null,
          stats: "",
          statsIcon: null,
        };
    }
  };

  const theme = getCardTheme();

  return (
    <Card
      sx={{
        mb: 3,
        cursor: "pointer",
        position: "relative",
        background: "#ffffff",
        borderRadius: "20px",
        overflow: "visible",
        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
        border: "1px solid rgba(255, 255, 255, 0.8)",
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "5px",
          background: theme.gradient,
          borderRadius: "20px 20px 0 0",
        },
        "&:hover": {
          transform: "translateY(-10px) scale(1.02)",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          "& .arrow-icon": {
            transform: "translateX(8px)",
            background: theme.gradient,
            color: "#fff",
          },
          "& .icon-wrapper": {
            transform: "rotate(10deg) scale(1.1)",
          },
          "& .stats-badge": {
            transform: "scale(1.05)",
          },
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent
        sx={{
          position: "relative",
          p: 4,
          "&:last-child": {
            pb: 4,
          },
        }}
      >
        {/* Top Section with Icon and Arrow */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          {/* Icon with background */}
          <Box
            className="icon-wrapper"
            sx={{
              width: 72,
              height: 72,
              borderRadius: "16px",
              background: theme.gradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: `0 8px 24px ${theme.iconColor}40`,
              transition: "all 0.3s ease",
            }}
          >
            {theme.icon}
          </Box>

          {/* Arrow Icon */}
          <IconButton
            className="arrow-icon"
            sx={{
              background: "rgba(0, 0, 0, 0.04)",
              color: theme.iconColor,
              transition: "all 0.3s ease",
              "&:hover": {
                background: theme.gradient,
                color: "#fff",
              },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>

        {/* Title with gradient text */}
        <Typography
          variant="h4"
          sx={{
            mb: 1,
            fontWeight: 700,
            background: theme.gradient,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: "-0.5px",
          }}
        >
          {amount}
        </Typography>

        {/* Stats Badge */}
        <Box
          className="stats-badge"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            mb: 2,
            borderRadius: "20px",
            background: `${theme.iconColor}15`,
            color: theme.iconColor,
            fontSize: "0.75rem",
            fontWeight: 600,
            transition: "all 0.3s ease",
          }}
        >
          {theme.statsIcon}
          {theme.stats}
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            lineHeight: 1.7,
            fontSize: "0.95rem",
            fontWeight: 400,
          }}
        >
          {description}
        </Typography>

        {/* Bottom decoration */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "60px",
            height: "3px",
            background: theme.gradient,
            borderRadius: "3px",
            opacity: 0.3,
          }}
        />
      </CardContent>
    </Card>
  );
};

export default SpaceAppCards;
